import { ColumnDef } from "@tanstack/react-table";
import { LinkArrow } from "assets/icons";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ItemsTable, PersonInfo } from "ui";

import { MissingItemsListItem } from "../../inventory.types";
import { InventoryProductVariant } from "../InventoryProductVariant";
import { MissingItemsTableProps } from "./MissingItemsTable.types";

import styles from "./MissingItemsTable.module.scss";

const MissingItemsTable = ({
  missingItems,
  sorting,
  setSorting,
  isLoading
}: MissingItemsTableProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const columns: Array<
    ColumnDef<MissingItemsListItem, MissingItemsListItem[]>
  > = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("Item"),
        size: 22,
        minSize: 22,
        cell: ({ row }) => (
          <InventoryProductVariant
            productVariant={missingItems[row.index].productVariant}
          />
        )
      },
      {
        header: t("Buying Price"),
        accessorKey: "buyingPrice",
        size: 9,
        minSize: 9,
        cell: ({ row }) => (
          <pre>
            {missingItems[row.index].productVariant.buyingPrice.toLocaleString(
              "en-US"
            )}{" "}
            FCFA
          </pre>
        )
      },
      {
        header: t("Date of notice"),
        accessorKey: "lossNoticedAt",
        size: 9,
        minSize: 9,
        cell: ({ row }) => (
          <pre>
            {missingItems[row.index].lossNoticedAt ? (
              <>
                {`${moment(missingItems[row.index].lossNoticedAt).format("DD/MM/YYYY")}`}
                {"\n"}
                {`${moment(missingItems[row.index].lossNoticedAt).format("HH:mm")}`}
              </>
            ) : (
              "-"
            )}
          </pre>
        )
      },
      {
        header: t("Manager Who Noticed Loss"),
        enableSorting: false,
        size: 18,
        minSize: 18,
        cell: ({ row }) => (
          <PersonInfo
            image={missingItems[row.index].inventoryManagerWhoNoticedLoss.image}
            dimensions={{ width: 40, height: 40, borderRadius: "100%" }}
            personName={`${
              missingItems[row.index].inventoryManagerWhoNoticedLoss.firstname
            } ${
              missingItems[row.index].inventoryManagerWhoNoticedLoss.lastname
            }`}
            nameClassName={styles.missingItemsTable_managerName}
            wrapperClassName={styles.missingItemsTable_managerCell}
          />
        )
      },
      {
        header: t("Box Name"),
        enableSorting: false,
        size: 11,
        minSize: 11,
        cell: ({ row }) =>
          missingItems[row.index].lostFromInventoryBox?.name ?? "-"
      },
      {
        header: "",
        accessorKey: "link",
        enableSorting: false,
        size: 7,
        minSize: 7,
        cell: ({ row }) => (
          <Link
            target="_blank"
            to={`/products/details/${missingItems[row.index]?.productVariant.categoryId}/${missingItems[row.index]?.productVariant.productId}/${missingItems[row.index]?.productVariant.id}/boxes`}
            className={styles.missingItemsTable_link}
          >
            {t("Link")} <LinkArrow />
          </Link>
        )
      }
    ],
    [missingItems, currentLanguage]
  );

  return (
    <ItemsTable
      className={styles.missingItemsTable}
      columns={columns}
      data={missingItems}
      sorting={sorting}
      skeletonHeight={135}
      setSorting={setSorting}
      isLoading={isLoading}
    />
  );
};

export default MissingItemsTable;
